import { PackingTypeSale } from 'enums/packingTypeSale';
import type Packing from 'model/Packing';
import { useMemo } from 'react';
import type { IGfpOffer } from 'types/api/GfpOffer';
import { formatFullDecimal } from 'utils/number';
import { calculateMinBySaleType } from 'utils/util';

export interface PurchaseInfoDisplay {
  qtdMinimaText: string;
  compositionLine: string;
  totalPedidoText: string;
  unitsPerType: number;
  totalUnits: number;
  unitsPerTrolley: number;
  exceedsTrolley: boolean;
  shippingFeeText: string;
}


function getSaleTypeLabel(
  typeSale: PackingTypeSale,
  quantity: number,
): string {
  switch (typeSale) {
    case PackingTypeSale.Layer:
      return quantity === 1 ? 'Camada' : 'Camadas';
    case PackingTypeSale.Trolley:
      return quantity === 1 ? 'Carrinho' : 'Carrinhos';
    default:
      return quantity === 1 ? 'Embalagem' : 'Embalagens';
  }
}

function buildShippingFeeText(shippingFee: number): string {
  return `Frete: R$ ${formatFullDecimal(shippingFee)}`;
}


export function usePurchaseInfoDisplay(
  selectedTypeSale: PackingTypeSale,
  packingSelected: Packing | undefined,
  quantity: number,
  shippingFee: number,
): PurchaseInfoDisplay {
  return useMemo(() => {
    const unitsPerPackage = packingSelected?.unitsPerPackage ?? 0;
    const packagingByLayer = packingSelected?.packagingByLayer ?? 0;
    const layersByTrolley = packingSelected?.layersByTrolley ?? 0;

    const unitsPerLayer = packagingByLayer * unitsPerPackage;
    const embalsByTrolley = packagingByLayer * layersByTrolley;
    const unitsPerTrolley = embalsByTrolley * unitsPerPackage;

    let unitsPerType = 0;
    switch (selectedTypeSale) {
      case PackingTypeSale.Layer:
        unitsPerType = unitsPerLayer;
        break;
      case PackingTypeSale.Trolley:
        unitsPerType = unitsPerTrolley;
        break;
      default:
        unitsPerType = unitsPerPackage;
    }

    const minQty = packingSelected
      ? calculateMinBySaleType(selectedTypeSale, packingSelected)
      : 0;
    const minLabel = getSaleTypeLabel(selectedTypeSale, minQty);
    const qtdMinimaText = `Qtd Mínima: ${minQty} ${minLabel}`;

    let compositionLine = '';
    switch (selectedTypeSale) {
      case PackingTypeSale.Packing:
        compositionLine = `Unidades por embalagem: ${unitsPerPackage} unidades`;
        break;
      case PackingTypeSale.Layer:
        compositionLine = `Camada: ${packagingByLayer} emb. × ${unitsPerPackage} un. = ${unitsPerLayer} unidades`;
        break;
      case PackingTypeSale.Trolley:
        compositionLine = `Carrinho: ${embalsByTrolley} emb. × ${unitsPerPackage} un. = ${unitsPerTrolley} unidades`;
        break;
      default:
        compositionLine = '';
    }

    const totalUnits = quantity > 0 ? quantity * unitsPerType : 0;
    let totalPedidoText = '';
    if (quantity > 0 && unitsPerType > 0) {
      const typeLabel = getSaleTypeLabel(selectedTypeSale, quantity);
      totalPedidoText = `Total do pedido: ${quantity} ${typeLabel} × ${unitsPerType} = ${totalUnits} un.`;
    }

    const exceedsTrolley = unitsPerTrolley > 0 && totalUnits > unitsPerTrolley;

    return {
      qtdMinimaText,
      compositionLine,
      totalPedidoText,
      unitsPerType,
      totalUnits,
      unitsPerTrolley,
      exceedsTrolley,
      shippingFeeText: buildShippingFeeText(shippingFee),
    };
  }, [selectedTypeSale, packingSelected, quantity, shippingFee]);
}


export function usePurchaseInfoDisplayGfp(
  selectedTypeSale: PackingTypeSale,
  gfpOffer: IGfpOffer | undefined,
  quantity: number,
  shippingFee: number,
): PurchaseInfoDisplay {
  return useMemo(() => {
    const unitsPerPackage = gfpOffer?.quantityPerPacking ?? 0;
    const packagingByLayer = gfpOffer?.quantityPerLayer ?? 0;
    const layersByTrolley = gfpOffer?.quantityPerTrolley ?? 0;

    const unitsPerLayer = packagingByLayer * unitsPerPackage;
    const embalsByTrolley = packagingByLayer * layersByTrolley;
    const unitsPerTrolley = embalsByTrolley * unitsPerPackage;

    let minQty = 0;
    switch (selectedTypeSale) {
      case PackingTypeSale.Layer:
        minQty = gfpOffer?.minAvailableLayerQuantity ?? 0;
        break;
      case PackingTypeSale.Trolley:
        minQty = gfpOffer?.minAvailableTrolleyQuantity ?? 0;
        break;
      default:
        minQty = gfpOffer?.minAvailablePackingsQuantity ?? 0;
    }

    let unitsPerType = 0;
    switch (selectedTypeSale) {
      case PackingTypeSale.Layer:
        unitsPerType = unitsPerLayer;
        break;
      case PackingTypeSale.Trolley:
        unitsPerType = unitsPerTrolley;
        break;
      default:
        unitsPerType = unitsPerPackage;
    }

    const minLabel = getSaleTypeLabel(selectedTypeSale, minQty);
    const qtdMinimaText = `Qtd Mínima: ${minQty} ${minLabel}`;

    let compositionLine = '';
    switch (selectedTypeSale) {
      case PackingTypeSale.Packing:
        compositionLine = `Unidades por embalagem: ${unitsPerPackage} unidades`;
        break;
      case PackingTypeSale.Layer:
        compositionLine = `Camada: ${packagingByLayer} emb. × ${unitsPerPackage} un. = ${unitsPerLayer} unidades`;
        break;
      case PackingTypeSale.Trolley:
        compositionLine = `Carrinho: ${embalsByTrolley} emb. × ${unitsPerPackage} un. = ${unitsPerTrolley} unidades`;
        break;
      default:
        compositionLine = '';
    }

    const totalUnits = quantity > 0 ? quantity * unitsPerType : 0;
    let totalPedidoText = '';
    if (quantity > 0 && unitsPerType > 0) {
      const typeLabel = getSaleTypeLabel(selectedTypeSale, quantity);
      totalPedidoText = `Total do pedido: ${quantity} ${typeLabel} × ${unitsPerType} = ${totalUnits} un.`;
    }

    const exceedsTrolley = unitsPerTrolley > 0 && totalUnits > unitsPerTrolley;

    return {
      qtdMinimaText,
      compositionLine,
      totalPedidoText,
      unitsPerType,
      totalUnits,
      unitsPerTrolley,
      exceedsTrolley,
      shippingFeeText: buildShippingFeeText(shippingFee),
    };
  }, [selectedTypeSale, gfpOffer, quantity, shippingFee]);
}
